import React from 'react';
import cx from 'classnames';
import { ESSButton } from '../../Atoms/ESSButton';
import { ContentWide, ContentWrapper, ContentNarrow } from 'volto-essvoltocomponents/components/Layout';
import PageSection from '../../Layout/PageSection';
import { RichText } from 'volto-essvoltocomponents/components/Atoms';
import './CTABanner.scss';

/**
 * CTA Banner Component
 *
 * A full-width call-to-action banner component with title, description, and action buttons.
 * Responsive layout that adapts from horizontal (desktop) to vertical (mobile).
 *
 * Based on ESS Design System - Figma component:
 * https://www.figma.com/design/6dZKp0OgtarsESIk3jLBa2/ESS-Design-System-Kelsey?node-id=2376-39791
 *
 * Example usage in "About Us" section of Homepage
 */

type CTAVariant = 'positive' | 'negative' | 'accent';
type ButtonSize = 'sm' | 'md';
type BannerVariant = 'default' ;
type Alignment = 'left' | 'center' | 'right';

interface CTABannerProps {
  /** Main heading text */
  title?: string;

  /** Supporting text/description */
  description?: string;

  /** Primary button text */
  ctaText?: string;

  /** Primary button external URL */
  ctaHref?: string;

  /** Primary button internal route */
  ctaTo?: string;

  /** Primary button click handler */
  ctaOnClick?: () => void;

  /** Primary button variant */
  ctaVariant?: CTAVariant;

  /** Button size */
  ctaSize?: ButtonSize;

  /** Visual style variant */
  variant?: BannerVariant;

  /** Horizontal alignment of the content in desktop*/
  alignment?: Alignment;

  /**Horizontal alignment of the content in mobile */
  mobileAlignment?: Alignment;

  /** Background color */
  backgroundColor?: string;

  /** Background image URL */
  backgroundImage?: string;

  /** Background video URL (mp4) */
  backgroundVideo?: string;

  /** Background video URL (webm fallback) */
  backgroundVideoWebm?: string;

  /** Poster image for video (shown while loading) */
  backgroundVideoPoster?: string;

  /** Text color for title and description (deprecated, use titleColor and descriptionColor instead) */
  textColor?: string;

  /** Title color (CSS color value or design token) */
  titleColor?: string;

  /** Description color (CSS color value or design token) */
  descriptionColor?: string;

  /** Minimum height of the banner (e.g., '400px', '50vh') */
  minHeight?: string;

  /** Overlay color (CSS color value, e.g. '#053280', 'rgba(0,0,0,0.5)') */
  overlayColor?: string;

  /** Overlay opacity (0 to 1) */
  overlayOpacity?: number;

  /** Additional CSS classes */
  className?: string;

  /** Additional HTML attributes */
  [key: string]: any;
}

const CTABanner: React.FC<CTABannerProps> = ({
  title,
  description,
  ctaText,
  ctaHref,
  ctaTo,
  ctaOnClick,
  ctaVariant = 'positive',
  ctaSize = 'md',
  variant = 'default',
  alignment = 'center',
  mobileAlignment = 'left',
  backgroundColor,
  backgroundImage,
  backgroundVideo,
  backgroundVideoWebm,
  backgroundVideoPoster,
  textColor,
  titleColor,
  descriptionColor,
  minHeight = '300px',
  overlayColor,
  overlayOpacity = 0.5,
  className,
  ...props
}) => {
  const bannerClasses = cx(
    'ess-cta-banner',
    `ess-cta-banner--${variant}`,
    {
      'ess-cta-banner--with-bg-image': backgroundImage,
      'ess-cta-banner--with-bg-video': backgroundVideo,
    },
    className,
  );

  const bannerTitleClasses = cx(
    'ess-cta-banner__title',
    `ess-cta-banner__title--desktop-${alignment}`,
    `ess-cta-banner__title--mobile-${mobileAlignment}`,
  );

  const sectionStyle: React.CSSProperties = {
    ...(backgroundColor && { backgroundColor }),
    ...(backgroundImage && {
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    }),
    ...(textColor && { color: textColor }),
    ...(minHeight && { minHeight }),
  };

  const titleStyle: React.CSSProperties = {
    ...(titleColor && { color: titleColor }),
    ...(textColor && !titleColor && { color: textColor }),
  };

  const descriptionStyle: React.CSSProperties = {
    ...(descriptionColor && { color: descriptionColor }),
    ...(textColor && !descriptionColor && { color: textColor }),
  };

  return (
    <PageSection padding={false} className={bannerClasses} style={sectionStyle} {...props}>
      {backgroundVideo && (
        <video
          className="ess-cta-banner__video"
          autoPlay
          loop
          muted
          playsInline
          poster={backgroundVideoPoster || undefined}
        >
          {backgroundVideoWebm && (
            <source src={backgroundVideoWebm} type="video/webm" />
          )}
          <source src={backgroundVideo} type="video/mp4" />
        </video>
      )}
      {overlayColor && (
        <div
          className="ess-cta-banner__overlay"
          style={{
            backgroundColor: overlayColor,
            opacity: overlayOpacity,
          }}
        />
      )}
      <ContentWrapper className="ess-cta-banner__container">
        <ContentWide 
          alignment={alignment}
          mobileAlignment={mobileAlignment}
          className="ess-cta-banner__content"
          gap="0">
          {title && (
            <h1 className={bannerTitleClasses}  style={titleStyle}>
              {title}
            </h1>
          )}

          <ContentNarrow 
            alignment={alignment}
            mobileAlignment={mobileAlignment}
            className="ess-cta-banner__description" >
            {description && (
              <RichText
                style={descriptionStyle} 
                content={description}
                alignment={alignment}
              />
            )}
          </ContentNarrow>
          {ctaText && (
            <div className="ess-cta-banner__actions">
              <ESSButton
                variant={ctaVariant}
                size={ctaSize}
                className="ess-cta-banner__cta"
                href={ctaTo || ctaHref || undefined}
              >
                {ctaText}
              </ESSButton>
            </div>
          )}
        </ContentWide>
      </ContentWrapper>
    </PageSection>
  );
};

export default CTABanner;
